import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { defaultLanguage } from '../../consts';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { AffiliateService } from '../../services/affiliate.service';
import { RedirectService } from '../../services/redirect.service';
import { StoryblokLoadComponentsService } from '../../services/storyblok-load-components.service';
import { mapStoryblokLang } from '../lib/lang-map';

export const storyblokStoryGuard: CanActivateFn = async route => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);
  const redirectService = inject(RedirectService);
  const affiliateService = inject(AffiliateService);

  const path = (getAllRouteData(route).storyblokPathOverride as string) || getFullPath(route).join('/') || 'home';

  const story = await firstValueFrom(storyblokService.getStory(path, route).pipe(map(resp => resp && resp.story)));

  const storyLang = mapStoryblokLang(story.lang);
  if (storyLang !== defaultLanguage) {
    const correctLangPath = story.translated_slugs
      .find(s => s.lang === storyLang)
      ?.path.replace(/home$/, '')
      .replace(/(?<!^)\/$/, '');
    const currentPath = getFullPath(route, false)
      .join('/')
      .replace(/\/page-\d+$/, '');
    if (correctLangPath && correctLangPath !== currentPath) {
      return redirectService.redirectTo([storyLang, ...correctLangPath.split('/')], {
        replaceUrl: true,
        queryParamsHandling: 'preserve',
        redirectType: 'permanent',
      });
    }
  }

  if (story.content.affiliate) {
    const relationships = await firstValueFrom(storyblokService.resolveRelationships(route));
    const affiliate = relationships.find(r => r.uuid === story.content.affiliate);
    if (route.queryParams.ref !== affiliate.slug) {
      return redirectService.redirectTo(story.full_slug.split('/'), {
        queryParams: {
          ...route.queryParams,
          source: affiliate.content.source,
          medium: affiliate.content.medium,
          campaign: affiliate.content.campaign,
          ref: affiliate.slug,
        },
      });
    }
  }

  if (route.queryParams.ref) {
    const affiliate = await affiliateService.getAffiliate(route.queryParams.ref);
    if (affiliate) {
      await affiliateService.handleAffiliate(affiliate);
    }
  }

  const componentTypes = await storyblokService.prepLoadComponents(story);
  await storyblokLoadComponentsService.loadComponents(componentTypes);
  if (getFullPath(route, false).pop()?.startsWith('page-')) {
    const blogPostLinkList = story.content.body?.find(c => c.component === 'blog_post_link_list');
    if (!blogPostLinkList || blogPostLinkList.posts?.length) {
      return redirectService.redirectTo(path.split('/'), { queryParams: route.queryParams });
    }
  }

  return true;
};
